









































































import Vue from 'vue';
import { Prop } from 'vue-property-decorator';
import Component from 'vue-class-component';
import FilterCluster from '@components/046-FilterCluster/FilterCluster.vue';
import ButtonComponent from '@components/004-ButtonComponent/ButtonComponent.vue';
import ClientOnly from 'vue-client-only';
import ResetSVG from '@assets/svg/reset-circle.svg';
import ControlsSVG from '@assets/svg/controls.svg';
import MagnifierSVG from '@assets/svg/magnifier.svg';
import DetectSVG from '@assets/svg/detect.svg';
import CloseSVG from '@assets/svg/x.svg';
import {
	FilterState,
	IContentFilter,
	IContentFilterGroup,
	IFilterableContentItem,
	IFilterPanelEmitDetails
} from '@frontend/types/modules/content-filter/IContentFilter';
import { FilterPanelEvent } from '@components/047-FilterPanel/constants';
import gsap from 'gsap';
import { pushToDataLayer } from '@frontend/utils/TrackingUtil';

import DOMTarget = gsap.DOMTarget;

@Component({
	hydration: true,
	componentId: 'C047',
	components: {
		FilterCluster,
		ButtonComponent,
		ControlsSVG,
		ResetSVG,
		ClientOnly,
		MagnifierSVG,
		DetectSVG,
		CloseSVG
	}
})
export default class FilterPanel extends Vue {
	@Prop(Object)
	protected readonly possibleFilters!: Record<string, string[]>;

	@Prop({ type: Array, default: () => [] })
	protected readonly filterableItems!: IFilterableContentItem[];

	@Prop({ type: String, default: 'default' })
	protected readonly variant?: string;

	@Prop(Boolean)
	protected readonly opened?: boolean;

	@Prop(Boolean)
	protected readonly visible?: boolean;

	@Prop(Boolean)
	protected readonly sortByHits?: boolean;

	@Prop(String)
	protected readonly className?: string;

	@Prop(Boolean)
	protected readonly showCounter?: boolean;

	@Prop(Boolean)
	protected readonly showSearch?: boolean;

	@Prop(String)
	protected readonly dataLayerEvent?: string;

	@Prop({ type: String, default: '' })
	protected searchtext?: string;

	protected hasSearchtext = false;

	protected hasNoResultsWithSearchtext = false;

	protected isOpen = this.opened;

	protected isVisible = this.visible;

	protected resultContainer?: HTMLElement | null;

	protected filterState = this.createFilterStateFromPossibleFilters();

	protected mounted(): void {
		this.setResultContainer();
		this.initResultContainerObserver();

		if (window.location.search) {
			this.applyFiltersFromQueryString(window.location.search);
		}

		this.update();
		this.addModifierOnModuleWrapper();
		this.initEvents();
	}

	// ---------------------------------------------------------------------- filters

	/**
	 * A lookup object in the format of possibleFilters with all checked attributes of the FilterCluster.
	 * It is used for convenience to have quicker access to the "checked" value of the various filter items.
	 */
	protected get checkedFilterAttributes(): Record<string, (string | number)[]> {
		const obj: Record<string, (string | number)[]> = {};

		this.filterState.forEach((stateGroup: IContentFilterGroup) => {
			obj[stateGroup.groupId] = stateGroup.filters
				.filter((stateFilter: IContentFilter) => stateFilter.checked)
				.map(stateFilter => stateFilter.value);
		});

		return obj;
	}

	/**
	 * Filtered content items, sorted by hits/occurrence.
	 */
	protected get filteredContentItems(): IFilterableContentItem[] {
		if (!this.hasAtLeastOneCheckedFilter) return [];

		const items = this.getContentItemsForCheckedFilterAttributes();

		if (this.sortByHits) {
			items.sort((a: IFilterableContentItem, b: IFilterableContentItem) => {
				const aCount = this.countMatches(a);
				const bCount = this.countMatches(b);

				return bCount - aCount;
			});
		}

		return items;
	}

	/**
	 * Get filtered content items derived by the attributes which are checked in the FilterCluster.
	 * @param omitGroup - Let a group pass the filter test, useful for generating lookup objects for the FilterCluster.
	 */
	protected getContentItemsForCheckedFilterAttributes(omitGroup?: string) {
		let items = [...this.filterableItems];
		const searchtext = this.searchtext as string;

		if (searchtext.length > 0) {
			items = items.filter(item => {
				let found = true;
				/**
				 * Hier die Logik für mehrere Suchwörter
				 */
				const parts = searchtext.split(' ');
				parts.forEach(part => {
					if (!item.attributes.fulltext.includes(part.toLowerCase())) {
						found = false;
					}
				});
				return found;
			});
			if (items.length === 0) {
				this.hasNoResultsWithSearchtext = true;
				this.hasSearchtext = false;
				items = [...this.filterableItems];
			} else {
				this.hasNoResultsWithSearchtext = false;
			}
		}

		Object.keys(this.possibleFilters).forEach(groupId => {
			items = items.filter(item => {
				const allUnchecked = this.checkedFilterAttributes[groupId].length < 1;

				if (allUnchecked || omitGroup === groupId) return true;

				if (!item.attributes[groupId]) return false;

				return item.attributes[groupId].some((filterValue): boolean => {
					return this.checkedFilterAttributes[groupId].includes(filterValue);
				});
			});
		});

		return items;
	}

	/**
	 * Helper function used in computed "filteredContentItems".
	 */
	protected countMatches(item: IFilterableContentItem): number {
		return Object.keys(item.attributes).reduce((acc, groupId): number => {
			const count = item.attributes[groupId].filter(filterValue =>
				this.checkedFilterAttributes[groupId].includes(filterValue)
			).length;
			return acc + count;
		}, 0);
	}

	protected get hasAtLeastOneCheckedFilter(): boolean {
		if (this.hasSearchtext === true) {
			return true;
		}
		return Object.keys(this.checkedFilterAttributes).some(groupId => {
			return this.checkedFilterAttributes[groupId].length > 0;
		});
	}

	protected get hasFilteredContentItems(): boolean {
		return this.filteredContentItems.length > 0;
	}

	/**
	 * To handle the rare case when no items are found even with faceted search.
	 */
	protected get hasNoResultsWithFilters(): boolean {
		return this.hasAtLeastOneCheckedFilter && !this.hasFilteredContentItems;
	}

	/**
	 * A lookup object for filtered content items per omitted group, derived by the attributes which are checked in
	 * the FilterCluster, but including ALL remaining content items of the respective group.
	 * So attributes of the same group does not filter each other.
	 */
	protected get filteredContentItemsForOmittedGroup(): Record<string, IFilterableContentItem[]> {
		const map: Record<string, IFilterableContentItem[]> = {};

		Object.keys(this.possibleFilters).forEach((groupId): void => {
			map[groupId] = this.getContentItemsForCheckedFilterAttributes(groupId);
		});

		return map;
	}

	/**
	 * A lookup object with remaining filterable attributes of the FilterCluster which are not excluded by
	 * filtered attributes of the other filter groups.
	 * It can be used for evaluating if a filter item has to be disabled (greyed out) and update the filter state.
	 */
	protected get remainingAttributes(): Record<string, (string | number)[]> {
		const map: Record<string, (string | number)[]> = {};

		Object.keys(this.possibleFilters).forEach((groupId): void => {
			map[groupId] = this.getRemainingAttributesForGroup(groupId);
		});

		return map;
	}

	/**
	 * Helper function used in computed "remainingAttributes".
	 * Returns an (deduplicated) array of all remaining filterable attributes of a filter group.
	 */
	protected getRemainingAttributesForGroup(groupId: string): (string | number)[] {
		const items = this.filteredContentItemsForOmittedGroup[groupId];
		const array = items.reduce((acc, item) => [...acc, ...item.attributes[groupId]], [] as string[]);
		return Array.from(new Set(array));
	}

	/**
	 * A lookup object for the number of matching attributes for the elements which are within the subset of
	 * the selected filters.
	 * Also the number of how many elements can be added to the result in case of an additive filter behavior
	 * when the attribute is inside the same group.
	 * It is used to update the count number for the filter items in the filter state.
	 */
	protected get numberOfAttributeMatches() {
		const map: Record<string, Record<string, number>> = {};

		Object.keys(this.possibleFilters).forEach((groupId): void => {
			map[groupId] = {
				...this.getNumberOfAttributeMatchesForGroup(groupId)
			};
		});

		return map;
	}

	/**
	 * Helper function used in computed "numberOfAttributeMatches".
	 */
	protected getNumberOfAttributeMatchesForGroup(groupId: string): Record<string, number> {
		const items = this.filteredContentItemsForOmittedGroup[groupId];
		const map: Record<string, number> = {};

		items.forEach((item): void => {
			item.attributes[groupId].forEach((attribute): void => {
				if (!map[attribute]) {
					map[attribute] = 1;
				} else {
					map[attribute]++;
				}
			});
		});

		return map;
	}

	protected applyDisabledStateToFilters(): void {
		this.filterState.forEach(group => {
			group.filters.forEach(filter => {
				const localFilter = filter;

				if (!this.hasAtLeastOneCheckedFilter) {
					localFilter.disabled = false;
					return;
				}

				const remainingAttributes = this.remainingAttributes[group.groupId] || [];

				localFilter.disabled = !remainingAttributes.includes(localFilter.value) && !localFilter.checked;
			});
		});
	}

	protected applyCountToFilters(): void {
		this.filterState.forEach(stateGroup => {
			stateGroup.filters.forEach(stateFilter => {
				const filter = stateFilter;

				if (this.hasNoResultsWithFilters) {
					filter.count = 0;
					return;
				}

				filter.count = this.numberOfAttributeMatches[stateGroup.groupId][stateFilter.value] || 0;
			});
		});
	}

	protected onFilterChange(filterState: IContentFilterGroup[]): void {
		this.pushFilterStateChangesToDataLayer(filterState, JSON.parse(JSON.stringify(this.filterState)));
		this.filterState = filterState;
		this.update();
	}

	protected resetFilters(): void {
		this.filterState.forEach((stateGroup: IContentFilterGroup) => {
			stateGroup.filters.forEach((stateFilter: IContentFilter) => {
				const filter = stateFilter;
				filter.checked = false;
				filter.disabled = false;
			});
		});
		this.resetSearchtext();
		this.update();
	}

	protected update(): void {
		this.applyQueryStringFromFilters();
		this.applyDisabledStateToFilters();
		this.applyHiddenStateToFilters();
		this.applyCountToFilters();
		this.dispatchFilterEvent();
		this.showOrHidedependendFilterGroup();
	}

	protected applyQueryStringFromFilters(): void {
		if (this.hasAtLeastOneCheckedFilter) {
			let queryString = '?';

			Object.keys(this.checkedFilterAttributes).forEach(groupId => {
				queryString += `&${groupId}=${this.checkedFilterAttributes[groupId].join(',')}`;
			});

			window.history.replaceState({}, '', queryString);
		} else {
			window.history.replaceState({}, '', window.location.pathname);
		}
	}

	protected applyFiltersFromQueryString(queryString: string): void {
		const searchParams = new URLSearchParams(queryString);

		searchParams.forEach((value, key) => {
			const filterGroup = this.filterState.find(stateGroup => stateGroup.groupId === key);

			if (filterGroup) {
				const values = value.split(',');

				filterGroup.filters.forEach(stateFilter => {
					const filter = stateFilter;
					filter.checked = values.includes(filter.value.toString());
				});
			}
		});
	}

	protected showOrHidedependendFilterGroup(): void {
		const filtersActive = this.checkedFilterAttributes;
		const filterGroup = this.filterState.find(stateGroup => stateGroup.groupId === 'city');
		if (filterGroup) {
			if (
				(filtersActive.jobType && filtersActive.jobType.length > 0) ||
				(filtersActive.section && filtersActive.section.length > 0)
			) {
				filterGroup.hidden = false;
			} else {
				filterGroup.hidden = true;
				if (filtersActive.city && filtersActive.city.length > 0) {
					filterGroup.filters.forEach((stateFilter: IContentFilter) => {
						const filter = stateFilter;
						filter.checked = false;
						filter.disabled = false;
					});
					this.update();
				}
			}
		}
	}

	/**
	 * Communicate with "FilterContainer" Component (and maybe other vanilla modules).
	 */
	protected dispatchFilterEvent(): void {
		let state: FilterState;

		if (this.hasNoResultsWithFilters) {
			state = 'has-no-results';
		} else if (this.hasFilteredContentItems) {
			state = 'is-filtered';
		} else {
			state = 'is-unfiltered';
		}

		const detail: IFilterPanelEmitDetails = {
			filteredElementIds: this.idsOfItemsToShow,
			activeFilters: this.checkedFilterAttributes,
			state
		};

		this.$el.dispatchEvent(
			new CustomEvent(FilterPanelEvent.FILTER_CHANGED, {
				detail
			})
		);
	}

	/**
	 * If there are any meaningful filtered items, show them.
	 * If no results were found by filtering, return an empty set -> display "no results page".
	 * Show all items as default (page load).
	 */
	protected get itemsToShow(): IFilterableContentItem[] {
		if (this.hasFilteredContentItems) return this.filteredContentItems;
		if (this.hasNoResultsWithFilters) return [];
		return this.filterableItems;
	}

	/**
	 * This is attached to the dispatch event and the data is used inside the FilterContainer component to handle
	 * the visibility of the items in the given list.
	 */
	protected get idsOfItemsToShow(): string[] {
		return this.itemsToShow.map(item => item.id);
	}

	/**
	 * Generates the reactive mutable state object for the FilterCluster component, as soon as the component initializes.
	 * Also setting the labels and i18n stuff happens here.
	 */
	protected createFilterStateFromPossibleFilters(): IContentFilterGroup[] {
		const kebabToPascal = (value: string) => {
			const arr = value.split('-');
			return arr.map((item: string) => item.charAt(0).toUpperCase() + item.slice(1)).join('');
		};

		const obj: IContentFilterGroup[] = [];

		Object.keys(this.possibleFilters).forEach((groupId: string) => {
			const label = this.i18n[`filterHeadline${kebabToPascal(groupId)}`];
			const filters: IContentFilter[] = [];

			this.possibleFilters[groupId].forEach((filterItem: string | number | IContentFilter) => {
				if (typeof filterItem === 'object') {
					filters.push({
						...filterItem,
						disabled: false,
						count: null
					});
				} else {
					filters.push({
						label:
							typeof filterItem === 'number'
								? filterItem.toString()
								: this.i18n[`filterLabel${kebabToPascal(filterItem)}`],
						value: filterItem,
						checked: false,
						disabled: false,
						count: null
					});
				}
			});

			obj.push({
				label,
				groupId,
				filters
			});
		});

		return obj;
	}

	// ---------------------------------------------------------------------- ui related

	protected togglePanel(): void {
		if (this.isOpen) {
			this.closePanel();
		} else {
			this.openPanel();
		}
	}

	protected openPanel(): void {
		this.isOpen = true;
		this.addModifierOnModuleWrapper();
	}

	protected closePanel(): void {
		this.isOpen = false;
		this.addModifierOnModuleWrapper();
	}

	protected async initResultContainerObserver(): Promise<void> {
		if (!this.resultContainer) return;

		if (!('IntersectionObserver' in window)) {
			await import('intersection-observer');
		}

		const options = {
			rootMargin: '0px 0px -50% 0px'
		};

		const callback = (entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry: IntersectionObserverEntry) => {
				const top = entry.boundingClientRect.top;
				this.isVisible = entry.isIntersecting || top < 0;
			});
		};

		const observer = new IntersectionObserver(callback, options);

		observer.observe(this.resultContainer);
	}

	/**
	 * To prevent race condition with initialization order of depended components.
	 */
	protected initEvents(): void {
		if (this.resultContainer) {
			this.resultContainer.addEventListener('ready', (): void => {
				this.dispatchFilterEvent();
			});

			// close when clicked outside filter-panel
			const mq = window.matchMedia('screen and (max-width: 980px)');
			document.addEventListener('click', (event): void => {
				const element = event.target as HTMLElement;
				const clickedInsideFilterPanel = !!element.closest('.c-filter-panel');

				if (this.isVisible && this.isOpen && mq.matches && !clickedInsideFilterPanel) {
					this.closePanel();
				}
			});
		}
	}

	/**
	 * @protected
	 */
	protected updateSearchtext() {
		if (this.searchtext && this.searchtext.length) {
			this.hasSearchtext = true;
			this.update();
		}
	}

	/**
	 * @protected
	 */
	protected updateSearchtextByKey() {
		if (this.searchtext && this.searchtext.length) {
			this.hasSearchtext = true;
			this.update();
		}
	}

	/**
	 * @protected
	 */
	protected resetSearchtext() {
		this.searchtext = '';
		this.hasSearchtext = false;
		this.update();
	}

	/**
	 * @protected
	 */
	protected resetSearchtextLight() {
		this.hasSearchtext = false;
		this.hasNoResultsWithSearchtext = false;
		this.update();
	}

	/**
	 * Find and set related results container, a sibling of this component and a child of the shared module wrapper,
	 * which is supposed to listen to the FilterPanel dispatch event and then displays filtered results.
	 */
	protected setResultContainer(): void {
		const parent = this.$el.parentNode;

		if (parent) {
			const wrapper = (parent as HTMLElement).closest('[data-init]') as HTMLElement;

			if (wrapper) {
				this.resultContainer = wrapper.querySelector('[data-filter-results]') as HTMLElement;
			}
		}
	}

	protected panelEnterAnimation(element: DOMTarget, done: GSAPCallback): void {
		gsap.set(element, { height: 'auto' });
		gsap.from(element, 0.3, {
			height: 0,
			onComplete: done,
			clearProps: true
		});
	}

	protected panelLeaveAnimation(element: DOMTarget, done: GSAPCallback): void {
		gsap.to(element, 0.2, {
			height: 0,
			onComplete: done
		});
	}

	protected addModifierOnModuleWrapper() {
		const parent = this.$el.parentNode as Element;
		const moduleWrapper = parent.closest('[data-init]');

		if (moduleWrapper) {
			if (this.isOpen) {
				moduleWrapper.setAttribute('data-has-filter-panel', 'open');
			} else {
				moduleWrapper.setAttribute('data-has-filter-panel', 'close');
			}
		}
	}

	protected applyHiddenStateToFilters(): void {
		this.filterState.forEach(group => {
			group.filters.forEach(filter => {
				const localFilter = filter;
				const checkedFilterAttributes = this.checkedFilterAttributes;
				let status = true;

				localFilter.hidden = false;
				if (localFilter.isHideable === true && localFilter.disabled === true) {
					const showWith = localFilter.showWith;
					if (showWith) {
						showWith.forEach(showgroup => {
							if (checkedFilterAttributes[showgroup.groupId].length > 0) {
								if (showgroup.items) {
									showgroup.items.forEach(item => {
										if (item === 'allentries') {
											status = false;
										} else if (checkedFilterAttributes[showgroup.groupId].includes(item)) {
											status = false;
										}
									});
								}
							}
						});
					}
					localFilter.hidden = status;
				}
			});
		});
	}

	private pushFilterStateChangesToDataLayer(
		newFilterState: IContentFilterGroup[],
		oldFilterState: IContentFilterGroup[]
	): void {
		const dataLayerEvent = this.dataLayerEvent;

		if (!dataLayerEvent) {
			return;
		}

		const push = (type: string, value: string): void => {
			pushToDataLayer({
				event: dataLayerEvent,
				// eslint-disable-next-line @typescript-eslint/camelcase
				filter_type: type,
				// eslint-disable-next-line @typescript-eslint/camelcase
				filter_value: value
			});
		};

		newFilterState.forEach(group => {
			const newCheckedGroupFilters = group.filters.filter(filter => filter.checked);
			const oldFilterGroup = oldFilterState.find(oldGroup => oldGroup.groupId === group.groupId);

			if (!oldFilterGroup) {
				return;
			}

			const oldCheckedGroupFilters = oldFilterGroup.filters.filter((filter: IContentFilter) => filter.checked);

			if (newCheckedGroupFilters.length !== oldCheckedGroupFilters.length) {
				push(
					group.groupId,
					newCheckedGroupFilters.length ? newCheckedGroupFilters.map(filter => filter.label).join('||') : 'all'
				);
			}
		});
	}
}
